import React, { memo } from 'react';
import { Nullable } from 'src/types/nullable.type';
import { Boundary, ResizeDragState } from './types';
import classNames from 'classnames';
import fastDeepEqual from 'fast-deep-equal';
import { animated, SpringValues, useTransition } from '@react-spring/web';
import { createPropsEqualsChecker } from './utils/create-props-equals-checker';
import { useEditorStore } from './editor-store';
import { useRefCallback } from 'src/utils/react/ref-callback.hook';
import { DraggableCore, DraggableData, DraggableEvent } from 'react-draggable';
import { locateBlockInGrid } from './layout-manager/locate-block-in-grid';
import { useAnimatedResizeBoundaries } from './utils/animated-resize-boundaries.hook';
import { useHover } from 'src/common/use-hover.hook';

type ResizeBoundaryStyleValues = { x: number; y: number; width: number; height: number; opacity: number };

type AnimatedResizeBoundaryViewProps = {
  boundary: Boundary;
  dragging: boolean;
  style: SpringValues<ResizeBoundaryStyleValues>;
  onStart: (boundary: Boundary, e: DraggableEvent, data: DraggableData) => void;
  onResize: (boundary: Boundary, e: DraggableEvent, data: DraggableData, stopping: boolean) => void;
};

const AnimatedResizeBoundaryView = ({
  boundary,
  dragging,
  style,
  onStart,
  onResize,
}: AnimatedResizeBoundaryViewProps) => {
  const draggingActiveRef = React.useRef(false);

  const onWrappedDragStart = (e: DraggableEvent, data: DraggableData) => {
    e.preventDefault();
    draggingActiveRef.current = true;
    onStart(boundary, e, data);
  };

  const onWrappedDrag = (e: DraggableEvent, data: DraggableData) => {
    // Running on raf causes an undesirable side-effect somehow where the
    // resize handle looks out of it's position between blocks/rows.
    // It's likely an issues with the way blocks positions are handled,
    // so we should resolve that first and then enable this again for a
    // smoother experience.

    // runOnRaf(() => {
    if (draggingActiveRef.current) {
      onResize(boundary, e, data, false);
    }
    // });
  };

  const onWrappedDragStop = (e: DraggableEvent, data: DraggableData) => {
    draggingActiveRef.current = false;
    onResize(boundary, e, data, true);
  };

  const ref = React.useRef<HTMLDivElement>(null);
  const hovering = useHover(ref);

  const { opacity, ...restStyle } = style;

  return (
    <DraggableCore onStart={onWrappedDragStart} onDrag={onWrappedDrag} onStop={onWrappedDragStop}>
      <animated.div
        className={classNames('absolute flex justify-center items-center transition-opacity', {
          'cursor-ew-resize flex-col': boundary.orientation === 'vertical',
          'cursor-ns-resize': boundary.orientation === 'horizontal',
          'z-neue-boundary-resize-dragging': dragging,
          'z-neue-boundary-resize': !dragging,
        })}
        ref={ref}
        style={{ ...restStyle, opacity: opacity.to((o) => (hovering ? 1 : o)) }}
      >
        <div
          className={classNames('bg-neue-journey-bg rounded-full')}
          style={{
            width: boundary.orientation === 'horizontal' ? '160px' : '100%',
            height: boundary.orientation === 'horizontal' ? '100%' : '80px',
          }}
        ></div>
      </animated.div>
    </DraggableCore>
  );
};

const MemoizedAnimatedResizeBoundaryView = memo(
  AnimatedResizeBoundaryView,
  createPropsEqualsChecker(['style'], 'AnimatedResizeBoundaryView')
);

const getResizeBoundaries = (boundaries: Boundary[]) =>
  boundaries.filter((b) => {
    if (b.orientation === 'horizontal') {
      return !b.isFirstInSection;
    }
    return !b.isFirstInRow && !b.isLastInRow;
  });

function createDefaultResizeDragState(): ResizeDragState {
  return {
    boundary: undefined,
    startBoundaryX: 0,
    startBoundaryY: 0,
    startX: 0,
    startY: 0,
    dx: 0,
    dy: 0,
  };
}

export const AnimatedResizeBoundaryViews = () => {
  const setIsDraggingSomethingOnEditor = useEditorStore((state) => state.setIsDraggingSomethingOnEditor);
  const dispatchUserEditorAction = useEditorStore((state) => state.dispatchUserEditorAction);
  const isDraggingSomethingOnEditor = useEditorStore((state) => state.isDraggingSomethingOnEditor);
  const hoverBlockId = useEditorStore((state) => state.hoverBlockId);
  const sectionGrids = useEditorStore((state) => state.layout.sectionGrids);
  const boundaries = useEditorStore((state) => state.layout.boundaries);
  const resizeBoundaries = React.useMemo(() => getResizeBoundaries(boundaries), [boundaries]);
  const resizeDragState = useEditorStore((state) => state.resizeDragState);
  const setResizeDragState = useEditorStore((state) => state.setResizeDragState);

  const hoverBlockLocation: { sectionIndex: number; rowIndex: number; colIndex: number } | undefined =
    React.useMemo(() => {
      if (!hoverBlockId) {
        return undefined;
      }
      const block = locateBlockInGrid(sectionGrids, hoverBlockId);
      if (!block) {
        return undefined;
      }
      return {
        sectionIndex: block.sectionIndex,
        rowIndex: block.rowIndex,
        colIndex: block.colIndex,
      };
    }, [hoverBlockId, sectionGrids]);

  const onResizeStart = useRefCallback(
    (boundary: Boundary, e: DraggableEvent, data: DraggableData) => {
      e.preventDefault();
      // console.log('onResizeStart');
      setResizeDragState({
        ...(resizeDragState || createDefaultResizeDragState()),
        boundary,
        startX: data.x,
        startY: data.y,
        startBoundaryX: boundary.x + boundary.width / 2,
        startBoundaryY: boundary.y + boundary.height / 2,
      });
      setIsDraggingSomethingOnEditor(true);
    },
    [resizeDragState]
  );

  const onResize = useRefCallback(
    (boundary: Boundary, e: DraggableEvent, data: DraggableData, stopping: boolean) => {
      // console.log('onResizeDrag', 'stopping', stopping);
      if (!resizeDragState || !resizeDragState.boundary) {
        return;
      }
      const { x, y } = data;
      const dx = x - resizeDragState.startX;
      const dy = y - resizeDragState.startY;
      if (boundary.orientation === 'horizontal') {
        if (dy !== 0) {
          dispatchUserEditorAction(
            {
              type: 'resize-vertical',
              boundary,
              bottomBoundaryY: resizeDragState.startBoundaryY + dy,
            },
            !stopping ? { syncable: false, undoable: false } : {}
          );
        }
      } else if (boundary.orientation === 'vertical') {
        if (dx !== 0) {
          dispatchUserEditorAction(
            {
              type: 'resize-horizontal',
              boundary,
              boundaryX: resizeDragState.startBoundaryX + dx,
            },
            !stopping ? { syncable: false, undoable: false } : {}
          );
        }
      }
      if (stopping) {
        setResizeDragState(null);
        setIsDraggingSomethingOnEditor(false);
      }
    },
    [resizeDragState]
  );

  const { springs, extendedResizeBoundaries } = useAnimatedResizeBoundaries(
    resizeBoundaries,
    resizeDragState || null,
    hoverBlockLocation || null,
    isDraggingSomethingOnEditor
  );

  return (
    <>
      {extendedResizeBoundaries.map((boundary, i) => {
        const spring = springs[i];
        return (
          <MemoizedAnimatedResizeBoundaryView
            key={boundary.boundaryKey}
            onStart={onResizeStart}
            onResize={onResize}
            boundary={boundary}
            dragging={boundary.dragging}
            style={spring}
          />
        );
      })}
    </>
  );
};

export const MemoizedAnimatedResizeBoundaryViews = memo(
  AnimatedResizeBoundaryViews,
  createPropsEqualsChecker([], 'AnimatedResizeBoundaryViews')
);
